import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserProfile, saveWorkoutSession, getWorkoutTemplate, getWarmupTemplate, getFinisherTemplate } from '../utils/database';
import { getExercises } from '../utils/adminData';
import { queueWorkout } from '../utils/offlineQueue';
import WorkoutFinishCard from '../components/WorkoutFinishCard';
import PlaceholderIcon from '../assets/icons/placeholder.svg';
import CheckCircleIcon from '../assets/icons/check-circle.svg';
import '../styles/Workout.css';

function Workout() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  const [, setLoading] = useState(true);
  
  // Workout session state
  const [sessionStatus, setSessionStatus] = useState('idle');
  const [startTime, setStartTime] = useState(null);
  const [pauseTime, setPauseTime] = useState(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Exercise drawer state
  const [activeExercise, setActiveExercise] = useState(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  
  // Finish screen state
  const [showFinishScreen, setShowFinishScreen] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  
  // Swipe-to-delete state
  const [swipedSetIndex, setSwipedSetIndex] = useState(null);
  const [swipeStartX, setSwipeStartX] = useState(null);
  const [swipeStartY, setSwipeStartY] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  
  // Page transition state
  const [isExiting, setIsExiting] = useState(false);
  
  // Exercises from Firestore (DB is source of truth)
  const [exercisesFromDb, setExercisesFromDb] = useState([]);
  useEffect(() => {
    getExercises()
      .then((data) => setExercisesFromDb(data || []))
      .catch((err) => console.error('[Workout] Failed to fetch exercises from Firestore:', err));
  }, []);

  // Create exercise lookup map for descriptions
  const exerciseLookup = useMemo(() => {
    const lookup = {};
    exercisesFromDb.forEach(ex => {
      lookup[ex.id] = ex;
      if (ex.name) lookup[ex.name.toLowerCase()] = ex;
    });
    return lookup;
  }, [exercisesFromDb]);
  
  // Check if an exercise uses bands
  const isBandExercise = useCallback((exercise) => {
    const exerciseData = exerciseLookup[exercise.exerciseId] || exerciseLookup[exercise.name?.toLowerCase()];
    return exerciseData?.equipment?.includes('bands') || false;
  }, [exerciseLookup]);
  
  // Check if an exercise is time-based (like plank, dead hang)
  const isTimedExercise = useCallback((exercise) => {
    const exerciseData = exerciseLookup[exercise.exerciseId] || exerciseLookup[exercise.name?.toLowerCase()];
    // Check for isometric category or known timed exercises
    if (exerciseData?.category === 'isometric') return true;
    // Also check for known timed exercise IDs
    const timedExerciseIds = ['dead_hang', 'plank', 'wall_sit', 'farmers_carry', 'side_plank'];
    return timedExerciseIds.includes(exercise.exerciseId) || timedExerciseIds.includes(exerciseData?.id);
  }, [exerciseLookup]);
  
  // Get full description for an exercise
  const getExerciseDescription = useCallback((exercise) => {
    // Try to find by exerciseId first
    const byId = exerciseLookup[exercise.exerciseId];
    if (byId?.description) return byId.description;
    
    // Try by name
    const byName = exerciseLookup[exercise.name.toLowerCase()];
    if (byName?.description) return byName.description;
    
    // Fallback to the short description in workout data
    return exercise.description || 'No description available.';
  }, [exerciseLookup]);
  
  // Get video URL for an exercise (returns YouTube embed URL or null)
  const getExerciseVideoUrl = useCallback((exercise) => {
    const byId = exerciseLookup[exercise.exerciseId];
    const byName = exerciseLookup[exercise.name?.toLowerCase()];
    const videoUrl = byId?.media?.video || byName?.media?.video;
    
    if (!videoUrl) return null;
    
    // Convert YouTube URLs to embed format
    // Handles: youtu.be/ID, youtube.com/watch?v=ID, youtube.com/embed/ID
    let videoId = null;
    
    if (videoUrl.includes('youtu.be/')) {
      // Short URL format: https://youtu.be/VIDEO_ID
      videoId = videoUrl.split('youtu.be/')[1]?.split(/[?&]/)[0];
    } else if (videoUrl.includes('youtube.com/watch')) {
      // Standard URL: https://www.youtube.com/watch?v=VIDEO_ID
      const urlParams = new URLSearchParams(videoUrl.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (videoUrl.includes('youtube.com/embed/')) {
      // Already embed format
      videoId = videoUrl.split('youtube.com/embed/')[1]?.split(/[?&]/)[0];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    return null;
  }, [exerciseLookup]);
  
  const timerRef = useRef(null);
  const drawerBodyRef = useRef(null);
  const drawerRef = useRef(null);
  const initialHeight = useRef(null);
  
  // Lock drawer position on open - prevent keyboard resize
  useEffect(() => {
    if (activeExercise && drawerRef.current) {
      if (!initialHeight.current) {
        initialHeight.current = window.innerHeight;
      }
      const topPos = Math.floor(initialHeight.current * 0.25);
      const height = initialHeight.current - topPos;
      drawerRef.current.style.top = `${topPos}px`;
      drawerRef.current.style.height = `${height}px`;
    }
  }, [activeExercise]);
  
  // Handle input focus - prevent scroll, let keyboard overlay, clear value for easy entry
  const handleInputFocus = (e) => {
    const input = e.target;
    input.setAttribute('readonly', 'readonly');
    setTimeout(() => {
      input.removeAttribute('readonly');
      input.focus();
      // Clear the value so user can immediately type new number
      input.value = '';
    }, 100);
  };
  
  // Get workout info from location state or use placeholder
  const workoutInfo = useMemo(() => {
    // Use today's date for fallback (not yesterday)
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    return location.state?.workout || {
      name: "Strength Session B",
      description: "Hinge + Pull focus",
      scheduledDate: todayStr,
      estimatedDuration: 60,
      workoutTemplateId: null,
    };
  }, [location.state]);

  // Load workout template + its warmup/finisher templates from Firestore.
  // Firestore is the single source of truth; if a referenced warmup/finisher
  // template doc is missing we degrade gracefully (that block is skipped).
  const [loadedTemplate, setLoadedTemplate] = useState(null);
  const [warmupTemplate, setWarmupTemplate] = useState(null);
  const [finisherTemplate, setFinisherTemplate] = useState(null);
  useEffect(() => {
    if (!workoutInfo.workoutTemplateId) {
      setLoadedTemplate(null);
      setWarmupTemplate(null);
      setFinisherTemplate(null);
      return;
    }
    let cancelled = false;
    getWorkoutTemplate(workoutInfo.workoutTemplateId)
      .then(async (t) => {
        if (cancelled) return;
        const blocks = (t && t.blocks) || [];
        const warmupBlock = blocks.find((b) => b.block_type === 'warmup' && b.template_id);
        const finisherBlock = blocks.find((b) => b.block_type === 'finisher' && b.template_id);
        const [w, f] = await Promise.all([
          warmupBlock ? getWarmupTemplate(warmupBlock.template_id) : Promise.resolve(null),
          finisherBlock ? getFinisherTemplate(finisherBlock.template_id) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setWarmupTemplate(w);
        setFinisherTemplate(f);
        setLoadedTemplate(t);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadedTemplate(null);
          setWarmupTemplate(null);
          setFinisherTemplate(null);
        }
      });
    return () => { cancelled = true; };
  }, [workoutInfo.workoutTemplateId]);

  // Convert workout template (object) to workout data structure
  const convertTemplateToWorkoutData = useCallback((template) => {
    if (!template || !template.blocks) return null;

    const workoutData = {
      warmup: [],
      workout: [],
      finisher: [],
      circuits: [], // Store circuit info for display
    };

    // Process blocks
    template.blocks.forEach(block => {
      if (block.block_type === 'warmup' && block.template_id) {
        const resolvedWarmup =
          warmupTemplate && warmupTemplate.id === block.template_id ? warmupTemplate : null;
        if (resolvedWarmup && Array.isArray(resolvedWarmup.exercises)) {
          resolvedWarmup.exercises.forEach(ex => {
            const exercise = exerciseLookup[ex.exercise_id];
            if (exercise) {
              workoutData.warmup.push({
                exerciseId: ex.exercise_id,
                name: exercise.name,
                description: ex.notes || '',
                sets: Array(ex.sets || 1).fill(0).map((_, i) => ({
                  setNumber: i + 1,
                  plannedReps: ex.reps || 10,
                  plannedWeight: 0,
                  actualReps: null,
                  actualWeight: null,
                  status: 'pending',
                })),
              });
            }
          });
        }
      } else if (block.block_type === 'main' && block.circuits) {
        // Process circuits
        block.circuits.forEach((circuit, circuitIndex) => {
          const circuitExercises = [];
          circuit.exercises.forEach(ex => {
            const exercise = exerciseLookup[ex.exercise_id];
            if (exercise) {
              const repsStr = ex.reps || '10';
              const reps = parseInt(repsStr.split('-')[0]) || parseInt(repsStr) || 10;
              const sets = circuit.rounds || 3;
              
              circuitExercises.push({
                exerciseId: ex.exercise_id,
                name: exercise.name,
                description: ex.notes || '',
                repsStr: ex.reps || '',
                circuitIndex: circuitIndex,
                circuitName: circuit.circuit_name,
                rounds: circuit.rounds,
                restBetween: circuit.rest_between_exercises,
                restAfter: circuit.rest_after_circuit,
                sets: Array(sets).fill(0).map((_, i) => ({
                  setNumber: i + 1,
                  plannedReps: reps,
                  plannedWeight: 0,
                  actualReps: null,
                  actualWeight: null,
                  status: 'pending',
                })),
              });
            }
          });
          workoutData.workout.push(...circuitExercises);
          workoutData.circuits.push({
            index: circuitIndex,
            name: circuit.circuit_name,
            rounds: circuit.rounds,
            restBetween: circuit.rest_between_exercises,
            restAfter: circuit.rest_after_circuit,
            exerciseCount: circuitExercises.length,
            startIndex: workoutData.workout.length - circuitExercises.length,
          });
        });
      } else if (block.block_type === 'finisher' && block.template_id) {
        const resolvedFinisher =
          finisherTemplate && finisherTemplate.id === block.template_id ? finisherTemplate : null;
        if (resolvedFinisher && Array.isArray(resolvedFinisher.exercises)) {
          resolvedFinisher.exercises.forEach(ex => {
            const exercise = exerciseLookup[ex.exercise_id];
            if (exercise) {
              workoutData.finisher.push({
                exerciseId: ex.exercise_id,
                name: exercise.name,
                description: ex.notes || ex.target || '',
                sets: Array(ex.sets || 3).fill(0).map((_, i) => ({
                  setNumber: i + 1,
                  plannedReps: ex.reps || ex.duration_seconds || 10,
                  plannedWeight: 0,
                  actualReps: null,
                  actualWeight: null,
                  status: 'pending',
                })),
              });
            }
          });
        }
      }
    });

    return workoutData;
  }, [exerciseLookup, warmupTemplate, finisherTemplate]);

  // Initialize workout data from loaded template or use placeholder
  const initialWorkoutData = useMemo(() => {
    if (loadedTemplate) {
      const templateData = convertTemplateToWorkoutData(loadedTemplate);
      if (templateData) return templateData;
    }
    
    // Fallback to placeholder data
    return {
      circuits: [],
    warmup: [
      {
        exerciseId: 'barbell_glute_bridge',
        name: 'Barbell Glute Bridge',
        description: 'Hip activation',
        sets: [
          { setNumber: 1, plannedReps: 10, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
      {
        exerciseId: 'loop_band_overhead_pull_apart',
        name: 'Loop Band Overhead Pull Apart',
        description: 'Shoulder activation',
        sets: [
          { setNumber: 1, plannedReps: 15, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 2, plannedReps: 15, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
      {
        exerciseId: 'dumbbell_glute_bridge',
        name: 'Dumbbell Glute Bridge',
        description: 'Hip activation',
        sets: [
          { setNumber: 1, plannedReps: 12, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 2, plannedReps: 12, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
    ],
    workout: [
      {
        exerciseId: 'romanian_deadlift',
        name: 'Romanian Deadlift',
        description: 'Hinge pattern, hamstring focus',
        sets: [
          { setNumber: 1, plannedReps: 10, plannedWeight: 95, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 2, plannedReps: 8, plannedWeight: 135, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 3, plannedReps: 8, plannedWeight: 155, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 4, plannedReps: 8, plannedWeight: 175, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
      {
        exerciseId: 'bent_over_barbell_row',
        name: 'Bent Over Barbell Row',
        description: 'Horizontal pull',
        sets: [
          { setNumber: 1, plannedReps: 10, plannedWeight: 65, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 2, plannedReps: 8, plannedWeight: 95, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 3, plannedReps: 8, plannedWeight: 115, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 4, plannedReps: 8, plannedWeight: 115, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
      {
        exerciseId: 'loop_band_lat_pulldown',
        name: 'Loop Band Lat Pulldown',
        description: 'Vertical pull',
        sets: [
          { setNumber: 1, plannedReps: 10, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 2, plannedReps: 10, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 3, plannedReps: 10, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
      {
        exerciseId: 'dumbbell_bicep_curl',
        name: 'Dumbbell Bicep Curl',
        description: 'Arm isolation',
        sets: [
          { setNumber: 1, plannedReps: 12, plannedWeight: 25, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 2, plannedReps: 12, plannedWeight: 25, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 3, plannedReps: 10, plannedWeight: 30, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
    ],
    finisher: [
      {
        exerciseId: 'loop_band_face_pull',
        name: 'Loop Band Face Pull',
        description: 'Rear delt and rotator cuff',
        sets: [
          { setNumber: 1, plannedReps: 15, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 2, plannedReps: 15, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 3, plannedReps: 15, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
      {
        exerciseId: 'dead_hang',
        name: 'Dead Hang',
        description: 'Grip and decompression',
        sets: [
          { setNumber: 1, plannedReps: 30, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
          { setNumber: 2, plannedReps: 30, plannedWeight: 0, actualReps: null, actualWeight: null, status: 'pending' },
        ],
      },
    ],
    };
  }, [loadedTemplate, convertTemplateToWorkoutData]);

  const [workoutData, setWorkoutData] = useState(initialWorkoutData);

  // When template loads (or changes), sync workout data from template
  useEffect(() => {
    if (loadedTemplate && initialWorkoutData?.workout?.length !== undefined) {
      setWorkoutData(initialWorkoutData);
    }
  }, [loadedTemplate, initialWorkoutData]);

  // Format time as MM:SS or HH:MM:SS
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Lock viewport on mount - native app experience
  useEffect(() => {
    const originalStyle = document.body.style.cssText;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.body.style.cssText = originalStyle;
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Timer effect
  useEffect(() => {
    if (sessionStatus === 'in_progress' && startTime) {
      timerRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime - totalPausedTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [sessionStatus, startTime, totalPausedTime]);

  const handleStart = useCallback(() => {
    setStartTime(Date.now());
    setSessionStatus('in_progress');
    setElapsedTime(0);
    setTotalPausedTime(0);
  }, []);

  const handlePause = useCallback(() => {
    setPauseTime(Date.now());
    setSessionStatus('paused');
  }, []);

  const handleResume = useCallback(() => {
    if (pauseTime) {
      setTotalPausedTime(prev => prev + (Date.now() - pauseTime));
    }
    setPauseTime(null);
    setSessionStatus('in_progress');
  }, [pauseTime]);

  const handleStop = useCallback(() => {
    setSessionStatus('stopped');
  }, []);

  const handleDiscard = useCallback(() => {
    setShowDiscardModal(true);
  }, []);

  // Animated navigation back to dashboard
  const navigateBack = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      navigate(`/${userId}/dashboard`);
    }, 300); // Match CSS animation duration
  }, [navigate, userId]);

  const confirmDiscard = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const handleFinish = useCallback(async () => {
    setSessionStatus('completed');
    setShowFinishScreen(true);
    setSaveStatus('saving');
    
    const sessionData = {
      scheduledActivityId: workoutInfo.activityId || null,
      workoutTemplateId: workoutInfo.workoutTemplateId || null,
      programId: workoutInfo.programId || null,
      name: workoutInfo.name,
      description: workoutInfo.description,
      scheduledDate: workoutInfo.scheduledDate,
      startTime: startTime,
      totalPausedTime: totalPausedTime,
    };
    
    try {
      // Try to save to Firestore
      const result = await saveWorkoutSession(userId, sessionData, workoutData);
      console.log('[Workout] Session saved:', result);
      setSaveStatus('saved');
    } catch (error) {
      console.error('[Workout] Error saving session:', error);
      // Only queue for later sync when offline. When online, show error (no "pending sync" flow).
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        try {
          await queueWorkout(userId, sessionData, workoutData);
          console.log('[Workout] Offline: session queued for later sync');
          setSaveStatus('saved');
        } catch (queueError) {
          console.error('[Workout] Failed to queue session:', queueError);
          setSaveStatus('error');
        }
      } else {
        setSaveStatus('error');
      }
    }
  }, [userId, workoutInfo, workoutData, startTime, totalPausedTime]);

  // Handle finish screen "Done" button
  const handleFinishScreenDone = useCallback(() => {
    navigate(`/${userId}/dashboard`, { 
      state: { justCompletedActivityId: workoutInfo.activityId }
    });
  }, [navigate, userId, workoutInfo.activityId]);

  // Get workout summary for finish screen
  const getWorkoutSummary = useCallback(() => {
    let totalSets = 0, completedSets = 0, totalReps = 0, totalWeight = 0;
    let exercisesCompleted = 0, totalExercises = 0;
    
    ['warmup', 'workout', 'finisher'].forEach(section => {
      workoutData[section]?.forEach(ex => {
        totalExercises++;
        let exerciseComplete = true;
        
        ex.sets.forEach(set => {
          totalSets++;
          if (set.status === 'completed') {
            completedSets++;
            const reps = set.actualReps || set.plannedReps || 0;
            const weight = set.actualWeight || set.plannedWeight || 0;
            totalReps += reps;
            totalWeight += reps * weight;
          } else {
            exerciseComplete = false;
          }
        });
        
        if (exerciseComplete && ex.sets.length > 0) {
          exercisesCompleted++;
        }
      });
    });
    
    return { 
      totalSets, 
      completedSets, 
      totalReps, 
      totalWeight,
      exercisesCompleted,
      totalExercises,
      duration: elapsedTime
    };
  }, [workoutData, elapsedTime]);

  // Update set values
  const handleSetUpdate = (section, exerciseIndex, setIndex, field, value) => {
    setWorkoutData(prev => {
      const updated = { ...prev };
      updated[section] = [...prev[section]];
      updated[section][exerciseIndex] = { ...prev[section][exerciseIndex] };
      updated[section][exerciseIndex].sets = [...prev[section][exerciseIndex].sets];
      updated[section][exerciseIndex].sets[setIndex] = {
        ...prev[section][exerciseIndex].sets[setIndex],
        [field]: value,
      };
      return updated;
    });
  };

  // Toggle set completion
  const handleCompleteSet = (section, exerciseIndex, setIndex) => {
    setWorkoutData(prev => {
      const updated = { ...prev };
      updated[section] = [...prev[section]];
      updated[section][exerciseIndex] = { ...prev[section][exerciseIndex] };
      updated[section][exerciseIndex].sets = [...prev[section][exerciseIndex].sets];
      const set = { ...prev[section][exerciseIndex].sets[setIndex] };
      
      if (set.status === 'completed') {
        // Toggle off
        set.status = 'pending';
      } else {
        // Toggle on - fill in defaults if empty
        if (!set.actualReps) set.actualReps = set.plannedReps;
        if (!set.actualWeight && set.plannedWeight > 0) set.actualWeight = set.plannedWeight;
        set.status = 'completed';
      }
      
      updated[section][exerciseIndex].sets[setIndex] = set;
      return updated;
    });
  };

  // Add a new set to an exercise
  const handleAddSet = (section, exerciseIndex) => {
    setWorkoutData(prev => {
      const updated = { ...prev };
      updated[section] = [...prev[section]];
      updated[section][exerciseIndex] = { ...prev[section][exerciseIndex] };
      const exercise = updated[section][exerciseIndex];
      const lastSet = exercise.sets[exercise.sets.length - 1];
      
      // Create new set based on the last set's planned values
      const newSet = {
        setNumber: exercise.sets.length + 1,
        plannedReps: lastSet?.plannedReps || 10,
        plannedWeight: lastSet?.plannedWeight || 0,
        actualReps: null,
        actualWeight: null,
        status: 'pending',
      };
      
      updated[section][exerciseIndex].sets = [...exercise.sets, newSet];
      return updated;
    });
  };

  // Delete a set from an exercise
  const handleDeleteSet = (section, exerciseIndex, setIndex) => {
    setWorkoutData(prev => {
      const updated = { ...prev };
      updated[section] = [...prev[section]];
      updated[section][exerciseIndex] = { ...prev[section][exerciseIndex] };
      
      // Remove the set at setIndex
      updated[section][exerciseIndex].sets = prev[section][exerciseIndex].sets.filter((_, idx) => idx !== setIndex);
      
      // Renumber remaining sets
      updated[section][exerciseIndex].sets = updated[section][exerciseIndex].sets.map((set, idx) => ({
        ...set,
        setNumber: idx + 1,
      }));
      
      return updated;
    });
    // Reset swipe state after deletion
    setSwipedSetIndex(null);
    setSwipeOffset(0);
  };

  // Swipe gesture handlers
  const handleSwipeStart = (e, setIndex) => {
    if (sessionStatus === 'idle') return;
    // Don't start swipe if touching an input or button
    const target = e.target;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button') || target.closest('input')) {
      return;
    }
    const touch = e.touches?.[0];
    if (touch) {
      e.stopPropagation();
      setSwipeStartX(touch.clientX);
      setSwipeStartY(touch.clientY);
      setSwipedSetIndex(setIndex);
      setSwipeOffset(0);
    }
  };

  const handleSwipeMove = (e, setIndex) => {
    if (sessionStatus === 'idle' || swipedSetIndex !== setIndex || swipeStartX === null || swipeStartY === null) return;
    const touch = e.touches?.[0];
    if (touch) {
      const deltaX = touch.clientX - swipeStartX;
      const deltaY = Math.abs(touch.clientY - swipeStartY);
      
      // Only handle swipe if horizontal movement is greater than vertical (swipe left)
      if (deltaX < 0 && Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 10) {
        e.preventDefault();
        e.stopPropagation();
        setSwipeOffset(Math.max(deltaX, -80)); // Max swipe distance of 80px
      } else if (deltaX > 0 && Math.abs(deltaX) > deltaY) {
        // Swiping right - reset
        setSwipeOffset(0);
      }
    }
  };

  const handleSwipeEnd = (setIndex) => {
    if (sessionStatus === 'idle' || swipedSetIndex !== setIndex) {
      setSwipeOffset(0);
      setSwipedSetIndex(null);
      setSwipeStartX(null);
      setSwipeStartY(null);
      return;
    }
    
    // If swiped more than 50px, trigger delete
    if (swipeOffset <= -50 && activeExercise) {
      const currentExercise = workoutData[activeExercise.section][activeExercise.exerciseIndex];
      // Only allow delete if more than one set
      if (currentExercise.sets.length > 1) {
        handleDeleteSet(activeExercise.section, activeExercise.exerciseIndex, setIndex);
      } else {
        // Reset if trying to delete last set
        setSwipeOffset(0);
        setSwipedSetIndex(null);
        setSwipeStartX(null);
        setSwipeStartY(null);
      }
    } else {
      // Reset swipe if not far enough
      setSwipeOffset(0);
      setSwipedSetIndex(null);
      setSwipeStartX(null);
      setSwipeStartY(null);
    }
  };

  // Reset swipe when drawer closes
  useEffect(() => {
    if (!activeExercise) {
      setSwipedSetIndex(null);
      setSwipeOffset(0);
      setSwipeStartX(null);
      setSwipeStartY(null);
    }
  }, [activeExercise]);

  // Calculate summary (currently unused but may be needed for future features)
  // eslint-disable-next-line no-unused-vars
  const getSummary = () => {
    let totalSets = 0, completedSets = 0, totalReps = 0, totalLbs = 0;
    ['warmup', 'workout', 'finisher'].forEach(section => {
      workoutData[section].forEach(ex => {
        ex.sets.forEach(set => {
          totalSets++;
          if (set.status === 'completed') {
            completedSets++;
            const reps = set.actualReps || set.plannedReps || 0;
            const weight = set.actualWeight || set.plannedWeight || 0;
            totalReps += reps;
            totalLbs += reps * weight;
          }
        });
      });
    });
    return { totalSets, completedSets, totalReps, totalLbs };
  };

  // Get exercise completion status
  const getExerciseStatus = (exercise) => {
    const completed = exercise.sets.filter(s => s.status === 'completed').length;
    return { completed, total: exercise.sets.length };
  };

  // Build labeled subtitle for circuit exercises (rounds, reps, rest times)
  const getExerciseSubtitle = (exercise, circuit) => {
    const parts = [];
    if (circuit?.rounds) parts.push(`${circuit.rounds} RNDs`);
    if (exercise.repsStr) parts.push(`${exercise.repsStr} reps`);
    if (circuit?.restBetween != null) parts.push(`${circuit.restBetween}s rest between`);
    if (circuit?.restAfter != null) parts.push(`${circuit.restAfter}s rest post`);
    const paramsLine = parts.join(' · ');
    const notes = (exercise.description || '').trim();
    const looksLikeParams = /^[\d\s]+$/.test(notes);
    if (notes && !looksLikeParams) {
      return paramsLine ? `${paramsLine} — ${notes}` : notes;
    }
    return paramsLine || null;
  };

  // Open exercise drawer
  const openExercise = (section, exerciseIndex) => {
    setActiveExercise({ section, exerciseIndex });
    setDescriptionExpanded(false); // Reset description state
  };

  // Close exercise drawer
  const closeExercise = () => {
    setActiveExercise(null);
    setDescriptionExpanded(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser || currentUser.uid !== userId) {
          navigate('/signin');
          return;
        }
        const userData = await getUserProfile(userId);
        if (!userData) {
          navigate('/onboarding');
          return;
        }
        setUser(userData);
        setLoading(false);
      } catch (error) {
        console.error('[Workout] Error:', error);
        navigate('/signin');
      }
    });
    return () => unsubscribe();
  }, [userId, navigate]);

  // Don't show loading screen - render layout immediately

  // const summary = getSummary(); // Unused for now
  const currentExercise = activeExercise 
    ? workoutData[activeExercise.section][activeExercise.exerciseIndex]
    : null;

  return (
    <div className={`page-dark workout-page ${isExiting ? 'exiting' : ''}`}>
      <div className="bg-glow" />
      <div className="bg-glow" />
      <div className="bg-overlay" />

      <div className="workout-main">
        {/* Back arrow - only shown before workout starts */}
        {sessionStatus === 'idle' && (
          <button 
            className="workout-back-button"
            onClick={navigateBack}
            aria-label="Back to dashboard"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
        )}

        {/* Header */}
        <div className="workout-header">
          <h1>{workoutInfo.name}</h1>
          <p className="text-muted">
              {(() => {
                // Parse date as local time (not UTC) to avoid timezone offset issues
                const [year, month, day] = workoutInfo.scheduledDate.split('-').map(Number);
                const localDate = new Date(year, month - 1, day);
                return localDate.toLocaleDateString('en-US', { 
                  weekday: 'long', month: 'long', day: 'numeric' 
                });
              })()}
              {workoutInfo.description && ` — ${workoutInfo.description}`}
          </p>
          {workoutInfo.weekLabel && (
            <p className="workout-week-label text-muted" style={{ marginTop: '0.25rem', fontStyle: 'italic' }}>
              {workoutInfo.weekLabel}
            </p>
          )}
        </div>

        {/* Controls */}
        {sessionStatus === 'idle' ? (
          <div className="workout-timer-bar workout-timer-bar--idle">
            <button className="c-ripple btn btn--primary btn--lg workout-start-button" onClick={handleStart}>Start Workout</button>
          </div>
        ) : (
          <div className="workout-timer-bar">
            <div className={`workout-timer ${sessionStatus === 'paused' ? 'paused' : ''}`}>
              {formatTime(elapsedTime)}
            </div>
            {sessionStatus === 'in_progress' && (
              <>
                <button className="c-ripple btn btn--secondary btn--sm" onClick={handlePause}>Pause</button>
                <button className="c-ripple btn btn--secondary btn--sm" onClick={handleStop}>Stop</button>
              </>
            )}
            {sessionStatus === 'paused' && (
              <>
                <button className="c-ripple btn btn--secondary btn--sm" onClick={handleResume}>Resume</button>
                <button className="c-ripple btn btn--secondary btn--sm" onClick={handleStop}>Stop</button>
              </>
            )}
            {sessionStatus === 'stopped' && (
              <div className="workout-stopped-controls">
                <div className="workout-stopped-buttons">
                  <button className="c-ripple btn btn--secondary btn--sm" onClick={handleResume}>Resume</button>
                  <button className="c-ripple btn btn--primary btn--sm" onClick={handleFinish}>Finish</button>
                </div>
                <button className="c-ripple workout-discard-link" onClick={handleDiscard}>Discard Workout</button>
              </div>
            )}
          </div>
        )}

        {/* Sections */}
        <div className="workout-sections">
          {/* Warmup */}
          <div className="workout-section">
            <h2 className="workout-section-title">Warmup</h2>
            <div className="workout-exercise-list">
              {workoutData.warmup.map((exercise, idx) => {
                const status = getExerciseStatus(exercise);
                return (
                  <div 
                    key={exercise.exerciseId}
                    className={`workout-exercise-card ${status.completed === status.total ? 'completed' : ''}`}
                    onClick={() => openExercise('warmup', idx)}
                  >
                    <div className="workout-exercise-card-info">
                      <span className="workout-exercise-card-name">{exercise.name}</span>
                      <span className="workout-exercise-card-desc">{exercise.description}</span>
                    </div>
                    <div className="workout-exercise-card-meta">
                      <span className="workout-exercise-card-sets">
                        {status.completed}/{status.total} sets
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Workout */}
          <div className="workout-section">
            <h2 className="workout-section-title">Workout</h2>
            {workoutData.circuits && workoutData.circuits.length > 0 ? (
              // Render with circuit grouping
              workoutData.circuits.map((circuit) => {
                const circuitExercises = workoutData.workout.filter(
                  ex => ex.circuitIndex === circuit.index
                );
                
                return (
                  <div key={circuit.index} className="workout-circuit-group">
                    <div className="workout-circuit-header">
                      <span className="workout-circuit-name">{circuit.name}</span>
                      {circuit.rounds && (
                        <span className="workout-circuit-rounds">{circuit.rounds} RNDs</span>
                      )}
                    </div>
                    <div className="workout-exercise-list workout-circuit-exercises">
                      {circuitExercises.map((exercise, exerciseIdx) => {
                        const status = getExerciseStatus(exercise);
                        const globalIdx = workoutData.workout.findIndex(e => e === exercise);
                        const isLastInCircuit = exerciseIdx === circuitExercises.length - 1;
                        const isFirstInCircuit = exerciseIdx === 0;
                        const subtitle = getExerciseSubtitle(exercise, circuit);
                        
                        return (
                          <div
                            key={exercise.exerciseId}
                            className={`workout-exercise-card workout-circuit-exercise ${status.completed === status.total ? 'completed' : ''} ${isFirstInCircuit ? 'circuit-first' : ''} ${isLastInCircuit ? 'circuit-last' : ''}`}
                            onClick={() => openExercise('workout', globalIdx)}
                          >
                            <div className="workout-exercise-card-info">
                              <span className="workout-exercise-card-name">{exercise.name}</span>
                              {subtitle && (
                                <div className="workout-exercise-card-prescription">
                                  <span className="workout-exercise-card-prescription-label">Circuit</span>
                                  <span className="workout-exercise-card-desc">{subtitle}</span>
                                </div>
                              )}
                            </div>
                            <div className="workout-exercise-card-meta">
                              <span className="workout-exercise-card-sets">
                                {status.completed}/{status.total} sets
                              </span>
                            </div>
                            {/* Connecting line on right side */}
                            {!isLastInCircuit && (
                              <div className="workout-circuit-connector"></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              // Fallback: render without circuit grouping
              <div className="workout-exercise-list">
                {workoutData.workout.map((exercise, idx) => {
                  const status = getExerciseStatus(exercise);
                  return (
                    <div 
                      key={exercise.exerciseId}
                      className={`workout-exercise-card ${status.completed === status.total ? 'completed' : ''}`}
                      onClick={() => openExercise('workout', idx)}
                    >
                      <div className="workout-exercise-card-info">
                        <span className="workout-exercise-card-name">{exercise.name}</span>
                        <span className="workout-exercise-card-desc">{exercise.description}</span>
                      </div>
                      <div className="workout-exercise-card-meta">
                        <span className="workout-exercise-card-sets">
                          {status.completed}/{status.total} sets
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Finisher */}
          <div className="workout-section">
            <h2 className="workout-section-title">Finisher</h2>
            <div className="workout-exercise-list">
              {workoutData.finisher.map((exercise, idx) => {
                const status = getExerciseStatus(exercise);
                return (
                  <div 
                    key={exercise.exerciseId}
                    className={`workout-exercise-card ${status.completed === status.total ? 'completed' : ''}`}
                    onClick={() => openExercise('finisher', idx)}
                  >
                    <div className="workout-exercise-card-info">
                      <span className="workout-exercise-card-name">{exercise.name}</span>
                      <span className="workout-exercise-card-desc">{exercise.description}</span>
                    </div>
                    <div className="workout-exercise-card-meta">
                      <span className="workout-exercise-card-sets">
                        {status.completed}/{status.total} sets
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Exercise Drawer (slides up from bottom) */}
      {activeExercise && currentExercise && (
        <div className="workout-drawer-overlay" onClick={closeExercise}>
          <div className="workout-drawer" ref={drawerRef} onClick={(e) => e.stopPropagation()}>
            <div className="workout-drawer-header">
              <div className="workout-drawer-title-row">
                <h3>{currentExercise.name}</h3>
                <button className="c-ripple workout-drawer-close" onClick={closeExercise}>×</button>
              </div>
              <div className="workout-drawer-description">
                <p className={descriptionExpanded ? 'expanded' : ''}>
                  {getExerciseDescription(currentExercise)}
                </p>
                <button 
                  className="workout-drawer-more" 
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                >
                  {descriptionExpanded ? 'less' : 'more'}
                </button>
                {descriptionExpanded && getExerciseVideoUrl(currentExercise) && (
                  <div className="workout-exercise-video">
                    <iframe
                      src={getExerciseVideoUrl(currentExercise)}
                      title={`${currentExercise.name} demonstration`}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="workout-drawer-body" ref={drawerBodyRef}>
              <div className="workout-sets-scroll-container">
                {currentExercise.sets.map((set, setIndex) => {
                  const isSwiped = swipedSetIndex === setIndex;
                  const canDelete = sessionStatus !== 'idle' && currentExercise.sets.length > 1;
                  
                  return (
                    <div 
                      key={setIndex} 
                      className="workout-set-row-wrapper"
                      onTouchStart={(e) => canDelete && handleSwipeStart(e, setIndex)}
                      onTouchMove={(e) => canDelete && handleSwipeMove(e, setIndex)}
                      onTouchEnd={() => canDelete && handleSwipeEnd(setIndex)}
                    >
                      <div 
                        className={`workout-set-row ${set.status === 'completed' ? 'completed' : ''} ${isSwiped ? 'swiped' : ''}`}
                        style={{
                          transform: isSwiped ? `translateX(${swipeOffset}px)` : 'translateX(0)',
                          transition: swipeStartX === null ? 'transform 0.2s ease-out' : 'none'
                        }}
                      >
                        <span className="workout-set-number">{set.setNumber}</span>
                        <div className="workout-set-inputs">
                          {isTimedExercise(currentExercise) ? (
                            /* Timed exercises: just seconds (no reps, no weight) */
                            <div className="workout-set-input-group workout-set-input-group--centered">
                              <input
                                type="number"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="workout-set-input"
                                placeholder={set.plannedReps || '—'}
                                value={set.actualReps || ''}
                                onChange={(e) => handleSetUpdate(
                                  activeExercise.section, 
                                  activeExercise.exerciseIndex, 
                                  setIndex, 
                                  'actualReps', 
                                  parseInt(e.target.value.replace(/\D/g, '')) || null
                                )}
                                onFocus={handleInputFocus}
                                onTouchStart={(e) => e.stopPropagation()}
                                onTouchMove={(e) => e.stopPropagation()}
                                disabled={sessionStatus === 'idle'}
                              />
                              <span className="workout-set-input-label">sec</span>
                            </div>
                          ) : (
                            /* Standard exercises: reps + weight/band */
                            <>
                              <div className="workout-set-input-group">
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  className="workout-set-input"
                                  placeholder={set.plannedReps || '—'}
                                  value={set.actualReps || ''}
                                  onChange={(e) => handleSetUpdate(
                                    activeExercise.section, 
                                    activeExercise.exerciseIndex, 
                                    setIndex, 
                                    'actualReps', 
                                    parseInt(e.target.value.replace(/\D/g, '')) || null
                                  )}
                                  onFocus={handleInputFocus}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  onTouchMove={(e) => e.stopPropagation()}
                                  disabled={sessionStatus === 'idle'}
                                />
                                <span className="workout-set-input-label">reps</span>
                              </div>
                              <div className="workout-set-input-group">
                                {isBandExercise(currentExercise) ? (
                                  <>
                                    <select
                                      className="workout-set-input workout-set-select"
                                      value={set.actualWeight || ''}
                                      onChange={(e) => handleSetUpdate(
                                        activeExercise.section, 
                                        activeExercise.exerciseIndex, 
                                        setIndex, 
                                        'actualWeight', 
                                        e.target.value || null
                                      )}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      onTouchMove={(e) => e.stopPropagation()}
                                      disabled={sessionStatus === 'idle'}
                                    >
                                      <option value="">—</option>
                                      <option value="light">Light</option>
                                      <option value="medium">Medium</option>
                                      <option value="heavy">Heavy</option>
                                    </select>
                                    <span className="workout-set-input-label">band</span>
                                  </>
                                ) : (
                                  <>
                                    <input
                                      type="number"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      className="workout-set-input"
                                      placeholder={set.plannedWeight > 0 ? set.plannedWeight : '—'}
                                      value={set.actualWeight || ''}
                                      onChange={(e) => handleSetUpdate(
                                        activeExercise.section, 
                                        activeExercise.exerciseIndex, 
                                        setIndex, 
                                        'actualWeight', 
                                        parseInt(e.target.value.replace(/\D/g, '')) || null
                                      )}
                                      onFocus={handleInputFocus}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      onTouchMove={(e) => e.stopPropagation()}
                                      disabled={sessionStatus === 'idle'}
                                    />
                                    <span className="workout-set-input-label">lbs</span>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          className={`workout-set-done ${set.status === 'completed' ? 'completed' : ''}`}
                          onClick={() => handleCompleteSet(
                            activeExercise.section, 
                            activeExercise.exerciseIndex, 
                            setIndex
                          )}
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                          disabled={sessionStatus === 'idle'}
                        >
                          <img 
                            src={set.status === 'completed' ? CheckCircleIcon : PlaceholderIcon} 
                            alt={set.status === 'completed' ? 'Completed' : 'Mark complete'} 
                          />
                        </button>
                      </div>
                      {canDelete && (
                        <div 
                          className="workout-set-delete-background"
                          style={{
                            opacity: isSwiped && swipeOffset < -20 ? Math.min(Math.abs(swipeOffset) / 80, 1) : 0
                          }}
                          onClick={() => handleDeleteSet(
                            activeExercise.section, 
                            activeExercise.exerciseIndex, 
                            setIndex
                          )}
                        >
                          <span>Delete</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="workout-drawer-footer">
              {sessionStatus !== 'idle' && (
                <button
                  className="workout-add-set-button"
                  onClick={() => handleAddSet(
                    activeExercise.section, 
                    activeExercise.exerciseIndex
                  )}
                >
                  + Add Set
                </button>
              )}
              {currentExercise.sets.every(set => set.status === 'completed') && (
                <button
                  className="c-ripple btn btn--primary workout-drawer-done"
                  onClick={closeExercise}
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Discard Confirmation Modal */}
      {showDiscardModal && (
        <div className="workout-modal-overlay">
          <div className="workout-modal">
            <h3>Are you sure?</h3>
            <p>Workout progress will not be saved.</p>
            <div className="workout-modal-buttons">
              <button className="c-ripple btn btn--secondary" onClick={() => setShowDiscardModal(false)}>
                No, go back
              </button>
              <button className="c-ripple btn btn--primary" onClick={confirmDiscard}>
                Yes, Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finish Workout Screen – same card as Dashboard "view completed" modal */}
      {showFinishScreen && (
        <WorkoutFinishCard
          summary={getWorkoutSummary()}
          saveStatus={saveStatus}
          onDone={handleFinishScreenDone}
          showCloseButton
        />
      )}
    </div>
  );
}

export default Workout;
